import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
  AfterViewInit,
  OnDestroy,
  HostListener,
  Type
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '@core/services';
import { LegalService } from '@core/services/legal/legal.service';
import { EulaComponent } from './eula/eula.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'app-legal-modal',
  templateUrl: './legal-modal.component.html',
  styleUrls: ['./legal-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeSwitch', [
      transition('* => *', [style({ opacity: 0 }), animate('200ms ease-in', style({ opacity: 1 }))])
    ])
  ]
})
export class LegalModalComponent implements AfterViewInit, OnDestroy {
  @Input() showAcceptButton = false;
  @Input() activeSection: 'eula' | 'privacy' = 'eula';
  @Output() termsAccepted = new EventEmitter<boolean>();

  @ViewChild('termsSection') termsSectionRef!: ElementRef<HTMLElement>;
  @ViewChild('privacySection') privacySectionRef!: ElementRef<HTMLElement>;

  showModal = false;
  canAccept = false;
  scrollPercent = 0;

  get sectionComponent(): Type<any> {
    return this.activeSection === 'eula' ? EulaComponent : PrivacyPolicyComponent;
  }

  private destroy$ = new Subject<void>();

  constructor(private authService: AuthService, private legalService: LegalService) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.termsSectionRef?.nativeElement?.focus();
    }, 0);
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: KeyboardEvent): void {
    if (this.showModal) {
      this.close();
    }
  }

  onScroll(container: HTMLElement): void {
    const { scrollTop, scrollHeight, clientHeight } = container;
    this.scrollPercent = (scrollTop / (scrollHeight - clientHeight)) * 100;
    this.canAccept = this.scrollPercent >= 99;
  }

  accept(): void {
    this.legalService
      .acceptTerms()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.termsAccepted.emit(true);
        this.showModal = false;
      });
  }

  close(): void {
    this.showModal = false;
  }

  async decline(): Promise<void> {
    this.showModal = false;

    if (this.showAcceptButton) {
      await this.authService.signOut(true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
